/**
 * Absence Form - Shared logic for modal and full-page forms.
 *
 * Four concerns:
 *   1. All-day toggle (time fields visibility).
 *   2. Validation clearing (eager-clear on input/change/changeDate).
 *   3. Flowbite Datepicker init on every ``[datepicker]`` input
 *      inside the form (Phase 17 — was inline in the modal partial).
 *   4. Cleaner search typeahead (Phase 17 — was inline). Source data
 *      comes from ``json_script#absence-form-config`` produced by
 *      ``apps/office/views/absences.py::_absence_form_js_config``.
 *
 * The modal modal-helper (``absences.js::initFormWidgets``) calls
 * ``initAbsenceForm()`` from its ``onContentInjected`` hook, so a
 * fresh datepicker / typeahead is bound on every modal open + every
 * 422 re-render. Per-element idempotency flags
 * (``_afDpBound`` / ``_afCsBound``) keep re-init a no-op on the same
 * DOM nodes.
 */

function readJsonScript(id, fallback) {
    const node = document.getElementById(id);
    if (!node) return fallback;
    try {
        return JSON.parse(node.textContent);
    } catch (e) { // eslint-disable-line no-unused-vars
        return fallback;
    }
}

// ========================================================
// ALL-DAY TOGGLE
// ========================================================

function initAllDayToggle() {
    const isAllDayCheckbox = document.getElementById('is_all_day');
    const timeFields = document.getElementById('time-fields');

    if (!isAllDayCheckbox || !timeFields) return;

    function toggleTimeFields() {
        timeFields.classList.toggle('hidden', isAllDayCheckbox.checked);
    }

    toggleTimeFields();
    isAllDayCheckbox.addEventListener('change', toggleTimeFields);
}

// ========================================================
// VALIDATION CLEARING
// ========================================================
// Removes the canonical .form-input-error class (from app.css) and the inline
// red error text once the user starts typing/selecting a valid value, so the
// drawer/modal does not stay visually "stuck" on a stale validation state
// while the user is actively correcting it.

function initValidation(formEl) {
    if (!formEl) return;

    function clearFieldError(field) {
        if (!field) return;
        field.classList.remove('form-input-error');

        const container = field.closest('div');
        if (container && container.parentElement) {
            const errorMsg = container.parentElement.querySelector('p.text-red-600, p.text-red-500');
            if (errorMsg) errorMsg.remove();
            const siblingError = container.parentElement.querySelector(':scope > p.text-red-600, :scope > p.text-red-500');
            if (siblingError) siblingError.remove();
        }
    }

    formEl.addEventListener('input', function (e) {
        const field = e.target;
        if ((field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') && field.value.trim()) {
            clearFieldError(field);
        }
    });

    formEl.addEventListener('change', function (e) {
        const field = e.target;
        if (field.tagName === 'SELECT' && field.value) clearFieldError(field);
        if (field.tagName === 'INPUT' && field.value.trim()) clearFieldError(field);
    });

    formEl.addEventListener('changeDate', function (e) {
        const field = e.target;
        if (field && field.value) clearFieldError(field);
    });

    formEl.querySelectorAll('[datepicker]').forEach(function (input) {
        input.addEventListener('blur', function () {
            if (this.value.trim()) clearFieldError(this);
        });
    });
}

// ========================================================
// FLOWBITE DATEPICKER (modal context — z-index handled in CSS)
// ========================================================

function initDatepickers(formEl) {
    if (!formEl) return;
    const inputs = formEl.querySelectorAll('[datepicker]');
    if (inputs.length === 0) return;

    function bindOne(input) {
        if (input._afDpBound) return;
        if (typeof window.Datepicker === 'undefined') return;
        input._afDpBound = true;
        try {
            input._datepicker = new window.Datepicker(input, {
                format: 'dd/mm/yyyy',
                autohide: true,
                todayBtn: true,
                clearBtn: true,
            });
        } catch (e) {
            console.warn('[absence-form] Datepicker init error:', e);
        }
    }

    if (typeof window.Datepicker !== 'undefined') {
        inputs.forEach(bindOne);
    } else {
        // Flowbite bundle may not have run its module factory yet on a
        // fresh modal open. Defer one tick so ``window.Datepicker`` is
        // populated, then bind. Same trick as the historical inline
        // ``setTimeout(150)``, just bound to a real ``rAF`` instead of
        // a magic 150 ms timer.
        window.requestAnimationFrame(() => {
            if (typeof window.Datepicker !== 'undefined') {
                inputs.forEach(bindOne);
            } else if (typeof window.reinitFlowbiteWidgets === 'function') {
                window.reinitFlowbiteWidgets(formEl);
            }
        });
    }
}

// ========================================================
// CLEANER SEARCH TYPEAHEAD (create-mode only)
// ========================================================

function escapeHtml(s) {
    return String(s === null || s === undefined ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function initCleanerSearch(formEl, cleaners, i18n) {
    if (!formEl) return;
    const searchInput = formEl.querySelector('#absence-cleaner-search');
    const idInput = formEl.querySelector('#absence-cleaner-id');
    const results = formEl.querySelector('#absence-cleaner-results');
    const existingClearBtn = formEl.querySelector('#absence-clear-cleaner-btn');

    // Edit mode (no cleaner select rendered) → nothing to do.
    if (!searchInput || !idInput || !results) return;
    if (searchInput._afCsBound) return;
    searchInput._afCsBound = true;

    const noResultsHtml = `<div class="px-4 py-3 text-helper">${escapeHtml(
        i18n.noCleanersFound || 'No cleaners found',
    )}</div>`;

    function renderResults(list) {
        if (list.length === 0) {
            results.innerHTML = noResultsHtml;
        } else {
            results.innerHTML = list
                .map((c) => {
                    const photoOrInitials = c.photo
                        ? `<img src="${escapeHtml(c.photo)}" alt="${escapeHtml(c.name)}" class="w-7 h-7 rounded-full object-cover flex-shrink-0">`
                        : `<div class="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-primary-600"><span class="text-xs font-medium text-white">${escapeHtml(c.initials)}</span></div>`;
                    return `<div class="cleaner-result-item flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" data-id="${escapeHtml(c.id)}" data-name="${escapeHtml(c.name)}">${photoOrInitials}<span class="text-sm text-gray-900 dark:text-white">${escapeHtml(c.name)}</span></div>`;
                })
                .join('');
        }
        results.classList.remove('hidden');
    }

    function clearFieldError(field) {
        if (!field) return;
        field.classList.remove('form-input-error');
        const container = field.closest('div');
        if (container && container.parentElement) {
            const err = container.parentElement.querySelector(
                'p.text-red-600, p.text-red-500',
            );
            if (err) err.remove();
        }
    }

    function clearSelection() {
        idInput.value = '';
        searchInput.value = '';
        results.classList.add('hidden');
        const clearBtn = formEl.querySelector('#absence-clear-cleaner-btn');
        if (clearBtn) clearBtn.remove();
    }

    function ensureClearButton() {
        if (formEl.querySelector('#absence-clear-cleaner-btn')) return;
        const clearBtn = document.createElement('button');
        clearBtn.type = 'button';
        clearBtn.id = 'absence-clear-cleaner-btn';
        clearBtn.className =
            'absolute inset-y-0 right-2 flex items-center justify-center w-6 h-6 my-auto rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 focus:outline-none';
        clearBtn.innerHTML =
            '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/></svg>';
        clearBtn.addEventListener('click', clearSelection);
        searchInput.parentElement.appendChild(clearBtn);
    }

    let searchTimeout = null;

    searchInput.addEventListener('input', function () {
        const query = this.value.toLowerCase().trim();
        if (searchTimeout) clearTimeout(searchTimeout);
        if (query.length < 1) {
            renderResults(cleaners);
            return;
        }
        searchTimeout = setTimeout(() => {
            renderResults(
                cleaners.filter((c) => c.name.toLowerCase().includes(query)),
            );
        }, 150);
    });

    searchInput.addEventListener('click', function () {
        const query = this.value.toLowerCase().trim();
        if (query.length < 1) {
            renderResults(cleaners);
        } else {
            renderResults(
                cleaners.filter((c) => c.name.toLowerCase().includes(query)),
            );
        }
    });

    results.addEventListener('click', function (e) {
        const item = e.target.closest('.cleaner-result-item');
        if (!item) return;
        searchInput.value = item.dataset.name;
        idInput.value = item.dataset.id;
        results.classList.add('hidden');
        clearFieldError(searchInput);
        ensureClearButton();
    });

    // Close on outside click — bound on document so it survives the
    // form's own re-renders. Idempotency via a per-search-input flag
    // (``_afCsOutsideBound``) so re-injecting the partial does not
    // double-bind.
    if (!searchInput._afCsOutsideBound) {
        searchInput._afCsOutsideBound = true;
        document.addEventListener('click', (e) => {
            if (!searchInput.contains(e.target) && !results.contains(e.target)) {
                results.classList.add('hidden');
            }
        });
    }

    if (existingClearBtn && !existingClearBtn._afCsClearBound) {
        existingClearBtn._afCsClearBound = true;
        existingClearBtn.addEventListener('click', clearSelection);
    }
}

// ========================================================
// MAIN INIT
// ========================================================

/**
 * @param {Object} [config]
 * @param {string} [config.formId] - Form element ID (default: 'absence-form')
 */
export function initAbsenceForm(config = {}) {
    const { formId = 'absence-form' } = config;
    return _initAbsenceForm({ formId });
}

/**
 * Auto-init hook for the standalone full-page absence form
 * (``/cleaners/absences/(create|<id>/edit)/``). The host template tags
 * its wrapper with ``data-auto-init="absenceForm"``; ``app.js`` calls
 * this on initial load and on every ``htmx:afterSwap`` for ``#main-content``,
 * so the form re-binds cleanly after a Pattern B boost. The modal-mode
 * variant continues to call ``initAbsenceForm`` directly from the
 * dynamic-modal ``onContentInjected`` hook (see ``absences.js``).
 */
export function autoInitAbsenceForm() {
    const root = document.querySelector('[data-auto-init="absenceForm"]');
    if (!root) return;
    _initAbsenceForm({ formId: 'absence-form' });
}

function _initAbsenceForm({ formId }) {

    initAllDayToggle();

    const formEl = document.getElementById(formId);
    if (!formEl) return;

    initValidation(formEl);
    initDatepickers(formEl);

    const cfg = readJsonScript('absence-form-config', {});
    const cleaners = Array.isArray(cfg.cleaners) ? cfg.cleaners : [];
    const i18n = cfg.i18n || {};
    initCleanerSearch(formEl, cleaners, i18n);
}
